import React, { useState, useEffect, useCallback } from 'react';
import Icon from './Icon';
import { apiFetch } from '../config/api';

const NOTIF_ICONS = {
  booking_new:       { icon: 'calendar',  color: '#1e3c72', bg: '#dbeafe' },
  booking_confirmed: { icon: 'confirmed', color: '#059669', bg: '#d1fae5' },
  booking_cancelled: { icon: 'close',     color: '#dc2626', bg: '#fee2e2' },
  booking_pending:   { icon: 'pending',   color: '#d97706', bg: '#fef3c7' },
  message_new:       { icon: 'chat',      color: '#7c3aed', bg: '#ede9fe' },
  review_new:        { icon: 'star',      color: '#f59e0b', bg: '#fef9c3' },
  b2b_approved:      { icon: 'check',     color: '#059669', bg: '#d1fae5' },
  b2b_rejected:      { icon: 'close',     color: '#dc2626', bg: '#fee2e2' },
  training_approved: { icon: 'staff',     color: '#0891b2', bg: '#cffafe' },
  franchise_approved:{ icon: 'franchisee',color: '#059669', bg: '#d1fae5' },
  system:            { icon: 'info',      color: '#6b7280', bg: '#f3f4f6' },
};

export const pushNotification = (userId, { type = 'system', title, text, link }) => {
  const key = `hairkus_notifs_${userId}`;
  const notifs = JSON.parse(localStorage.getItem(key) || '[]');
  notifs.unshift({ id: Date.now(), type, title, text, link, read: false, createdAt: new Date().toISOString() });
  localStorage.setItem(key, JSON.stringify(notifs.slice(0, 50)));
  window.dispatchEvent(new Event('hairkus-notif-update'));
  window.dispatchEvent(new StorageEvent('storage', { key, newValue: JSON.stringify(notifs.slice(0, 50)) }));
};

export const getUnreadNotifCount = async (userId) => {
  try {
    const data = await apiFetch('/notifications');
    return (data || []).filter(n => !n.is_read).length;
  } catch {
    return 0;
  }
};

export const useNotifications = (userId) => {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await apiFetch('/notifications');
      const normalized = (data || []).map(n => ({
        id: n.id,
        type: n.type,
        title: n.title,
        text: n.text,
        link: n.link,
        read: n.is_read,
        createdAt: n.created_at,
      }));
      setNotifs(normalized);
      setUnread(normalized.filter(n => !n.read).length);
    } catch { }
  }, [userId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    window.addEventListener('hairkus-notif-update', load);
    return () => { clearInterval(id); window.removeEventListener('hairkus-notif-update', load); };
  }, [load]);

  const markRead = useCallback(async (notifId) => {
    try {
      await apiFetch(`/notifications/${notifId}/read`, { method: 'PATCH' });
      load();
    } catch { }
  }, [load]);

  const markAllRead = useCallback(async () => {
    try {
      await apiFetch('/notifications/read-all', { method: 'PATCH' });
      load();
    } catch { }
  }, [load]);

  const deleteNotif = useCallback(async (notifId) => {
    try {
      await apiFetch(`/notifications/${notifId}`, { method: 'DELETE' });
      load();
    } catch { }
  }, [load]);

  const clearAll = useCallback(async () => {
    try {
      await apiFetch('/notifications/clear-all', { method: 'DELETE' });
      load();
    } catch { }
  }, [load]);

  return { notifs, unread, markRead, markAllRead, deleteNotif, clearAll };
};

const NotificationsPanel = ({ user }) => {
  const { notifs, unread, markRead, markAllRead, deleteNotif, clearAll } = useNotifications(user?.id);
  const [filter, setFilter] = useState('all'); 

  const shown = filter === 'unread' ? notifs.filter(n => !n.read) : notifs;

  const fmt = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'przed chwilą';
    if (diff < 3600) return `${Math.floor(diff/60)} min temu`;
    if (diff < 86400) return `${Math.floor(diff/3600)} godz. temu`;
    return d.toLocaleDateString('pl-PL', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
  };

  useEffect(() => {
    if (!user?.id) return;
    const key = `hairkus_notifs_${user.id}`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    if (existing.length === 0) {
      const demos = [
        { id: Date.now()-5000, type:'booking_new',       title:'Nowa rezerwacja!',          text:'Klient zarezerwował wizytę na jutro o 10:00', read:false, createdAt: new Date(Date.now()-300000).toISOString() },
        { id: Date.now()-4000, type:'message_new',       title:'Nowa wiadomość',             text:'Klient zadał pytanie dotyczące zabiegu', read:false, createdAt: new Date(Date.now()-600000).toISOString() },
        { id: Date.now()-3000, type:'review_new',        title:'Nowa opinia ★★★★★',         text:'Klient zostawił opinię 5/5 po ostatniej wizycie', read:true, createdAt: new Date(Date.now()-3600000).toISOString() },
        { id: Date.now()-2000, type:'system',            title:'Witaj w HAIRKUS!',            text:'System powiadomień jest aktywny. Bądź na bieżąco.', read:true, createdAt: new Date(Date.now()-86400000).toISOString() },
      ].filter(() => ['salon','admin'].includes(user?.role));
      if (demos.length) localStorage.setItem(key, JSON.stringify(demos));
    }
  }, [user?.id, user?.role]);

  return (
    <div className="notif-wrapper">
      {/* Nagłówek */}
      <div className="notif-header">
        <div className="notif-header-left">
          <div className="notif-header-icon"><Icon name="notification" color="white" size="xl" /></div>
          <div>
            <h2>Powiadomienia</h2>
            <p>{unread > 0 ? <span className="notif-unread-count">{unread} nieprzeczytanych</span> : 'Wszystkie przeczytane'}</p>
          </div>
        </div>
        <div className="notif-header-actions">
          {unread > 0 && (
            <button className="notif-mark-all-btn" onClick={markAllRead}>
              <Icon name="check" style={{marginRight:'6px'}} />Oznacz wszystkie
            </button>
          )}
          {notifs.length > 0 && (
            <button className="notif-clear-btn" onClick={clearAll}>
              <Icon name="delete" style={{marginRight:'6px'}} />Wyczyść
            </button>
          )}
        </div>
      </div>

      {/*  Filtr - zakładki */}
      <div className="notif-filter-tabs">
        <button className={`notif-filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          Wszystkie <span className="notif-tab-count">{notifs.length}</span>
        </button>
        <button className={`notif-filter-tab ${filter === 'unread' ? 'active' : ''}`} onClick={() => setFilter('unread')}>
          Nieprzeczytane {unread > 0 && <span className="notif-tab-count unread">{unread}</span>}
        </button>
      </div>

      {/* Lista powiadomień */}
      {shown.length === 0 ? (
        <div className="notif-empty">
          <Icon name="notification" color="#d1d5db" size="3x" />
          <h3>{filter === 'unread' ? 'Brak nieprzeczytanych' : 'Brak powiadomień'}</h3>
          <p>Tutaj pojawią się informacje o rezerwacjach, wiadomościach i aktualizacjach</p>
        </div>
      ) : (
        <div className="notif-list">
          {shown.map(n => {
            const cfg = NOTIF_ICONS[n.type] || NOTIF_ICONS.system;
            return (
              <div key={n.id} className={`notif-item ${!n.read ? 'unread' : ''}`}
                onClick={() => !n.read && markRead(n.id)}>
                <div className="notif-icon-wrap" style={{background: cfg.bg}}>
                  <Icon name={cfg.icon} color={cfg.color} />
                  {!n.read && <span className="notif-item-dot" />}
                </div>
                <div className="notif-content">
                  <div className="notif-title">{n.title}</div>
                  <div className="notif-text">{n.text}</div>
                  <div className="notif-time">{fmt(n.createdAt)}</div>
                </div>
                <button className="notif-delete-btn" onClick={e => { e.stopPropagation(); deleteNotif(n.id); }}>
                  <Icon name="close" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
